import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { useAddImageMutation, useShopDataQuery } from "@/query/shopDataQuery"
import { Cross2Icon, ImageIcon, UploadIcon } from "@radix-ui/react-icons"
import React, { useRef, useState } from "react"

type AddImageModalProps = {
  isOpen: boolean
  onClose: () => void
  replaceIndex?: number // Optionnel : index pour remplacer une image existante
}

export const AddImageModal: React.FC<AddImageModalProps> = ({
  isOpen,
  onClose,
  replaceIndex,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { shopData } = useShopDataQuery()
  const currentImages = shopData?.urlsImage || []
  const isMaxImagesReached =
    replaceIndex === undefined && currentImages.length >= 7

  const {
    addImageMutation,
    isUploadingImage,
    uploadImageError,
    isUploadError,
  } = useAddImageMutation()

  const handleFileSelect = (file: File) => {
    // Validation des types de fichiers
    const supportedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "image/gif",
    ]
    if (!supportedTypes.includes(file.type)) {
      alert("Type de fichier non supporté. Utilisez JPG, PNG, WebP ou GIF.")
      return
    }

    // Validation de la taille (5MB max)
    const maxSize = 5 * 1024 * 1024 // 5MB
    if (file.size > maxSize) {
      alert(
        `Fichier trop volumineux. Taille maximum: 5MB, reçu: ${
          Math.round((file.size / 1024 / 1024) * 100) / 100
        }MB`
      )
      return
    }

    setSelectedFile(file)
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleSave = () => {
    if (selectedFile) {
      addImageMutation(
        {
          file: selectedFile,
          replaceIndex: replaceIndex,
        },
        {
          onSuccess: () => {
            handleClose()
          },
          onError: (error) => {
            console.error("Erreur upload image:", error)
          },
        }
      )
    }
  }

  const handleClose = () => {
    setSelectedFile(null)
    setPreviewUrl(null)
    setIsDragOver(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
    onClose()
  }

  const removeSelectedFile = () => {
    setSelectedFile(null)
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
      setPreviewUrl(null)
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const openFileSelector = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    } else {
      console.error("fileInputRef.current is null")
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open: boolean) => !open && handleClose()}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <ImageIcon className="h-5 w-5 mr-2 text-blue-500" />
            {replaceIndex !== undefined
              ? "Remplacer l'image"
              : "Ajouter une nouvelle image"}
          </DialogTitle>
          <DialogDescription>
            {replaceIndex !== undefined
              ? "Sélectionnez une nouvelle image pour remplacer l'image existante."
              : "Sélectionnez une image à ajouter à la galerie de votre boutique."}{" "}
            Formats acceptés : JPG, PNG, WebP, GIF (max 5MB).
          </DialogDescription>
        </DialogHeader>

        {/* Input de fichier unique pour tout le modal */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
          onChange={handleFileInputChange}
          style={{ display: "none" }}
        />

        <div className="space-y-6">
          {/* Alerte pour limite d'images atteinte */}
          {isMaxImagesReached && (
            <Alert variant="destructive">
              <AlertDescription>
                Limite d'images atteinte ({currentImages.length}/7). Vous devez
                d'abord supprimer une image existante pour en ajouter une
                nouvelle.
              </AlertDescription>
            </Alert>
          )}

          {/* Affichage des erreurs */}
          {isUploadError && uploadImageError && (
            <Alert variant="destructive">
              <AlertDescription>
                Erreur lors de l'upload :{" "}
                {uploadImageError.message ||
                  "Une erreur inconnue s'est produite"}
              </AlertDescription>
            </Alert>
          )}

          {/* Zone de sélection de fichier */}
          <div className="space-y-3">
            <Label>Sélectionner une image</Label>

            {!selectedFile ? (
              <div
                className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
                  isMaxImagesReached
                    ? "border-slate-200 dark:border-slate-700 cursor-not-allowed opacity-50"
                    : isDragOver
                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 cursor-pointer"
                    : "border-slate-300 dark:border-slate-600 cursor-pointer hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                }`}
                onDragOver={!isMaxImagesReached ? handleDragOver : undefined}
                onDragLeave={!isMaxImagesReached ? handleDragLeave : undefined}
                onDrop={!isMaxImagesReached ? handleDrop : undefined}
                onClick={!isMaxImagesReached ? openFileSelector : undefined}
              >
                <div className="flex flex-col items-center space-y-4">
                  <div
                    className={`w-16 h-16 rounded-full flex items-center justify-center ${
                      isMaxImagesReached
                        ? "bg-slate-100 dark:bg-slate-700"
                        : "bg-blue-100 dark:bg-blue-900/30"
                    }`}
                  >
                    <UploadIcon
                      className={`h-8 w-8 ${
                        isMaxImagesReached ? "text-slate-400" : "text-blue-500"
                      }`}
                    />
                  </div>
                  <div>
                    <p
                      className={`text-lg font-medium ${
                        isMaxImagesReached
                          ? "text-slate-400 dark:text-slate-500"
                          : "text-slate-700 dark:text-slate-200"
                      }`}
                    >
                      {isMaxImagesReached
                        ? "Limite d'images atteinte"
                        : "Glissez-déposez votre image ici"}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {isMaxImagesReached
                        ? `${currentImages.length}/7 images utilisées`
                        : "ou cliquez pour parcourir vos fichiers"}
                    </p>
                  </div>
                  {!isMaxImagesReached && (
                    <div className="text-xs text-slate-400 dark:text-slate-500">
                      Taille maximale : 5 MB
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Aperçu de l'image sélectionnée */}
                <div className="relative">
                  <div className="aspect-[4/3] w-full overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
                    <img
                      src={previewUrl || ""}
                      alt="Aperçu"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <button
                    onClick={removeSelectedFile}
                    className="absolute top-2 right-2 p-1 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors duration-200"
                    aria-label="Supprimer l'image"
                    disabled={isUploadingImage}
                  >
                    <Cross2Icon className="h-4 w-4" />
                  </button>
                </div>

                {/* Informations sur le fichier */}
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                        {selectedFile.name}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={openFileSelector}
                      disabled={isUploadingImage}
                    >
                      Changer
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="sm:justify-start pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isUploadingImage}
          >
            Annuler
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!selectedFile || isUploadingImage || isMaxImagesReached}
            className="bg-blue-500 hover:bg-blue-600 text-white"
          >
            {isUploadingImage ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Upload en cours...
              </>
            ) : (
              <>
                <ImageIcon className="h-4 w-4 mr-2" />
                {replaceIndex !== undefined
                  ? "Remplacer l'image"
                  : "Ajouter l'image"}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
